import { useArchiveOffer, useRemoveSocialOfferSpotlight } from "@api";
import type { IChallenge } from "@bbj-types/challenge";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ObjectId } from "@bbj-types/utils";
import { useAddFavoriteOffer } from "@root/api/services/challenges/addFavoriteOffer";
import { useDeleteFavoriteOffer } from "@root/api/services/challenges/deleteFavoriteOffer";
import { useGlobalContext } from "@root/context";
import { useAuthorizer, useChallengesByTags } from "@styleguide";
import { isAxiosError } from "axios";
import type { IDrawerSocialOfferHeaderProps } from ".";

export const useDrawerSocialOfferActions = <T extends ISocialOffer>(
  offer: T,
  onUpdateOffer?: IDrawerSocialOfferHeaderProps<T>["onUpdateOffer"]
) => {
  const { challenges } = useGlobalContext();
  const [, archiveOffer] = useArchiveOffer();
  const isAuthorizedAddFavorite = useAuthorizer(["candidate.socialboard.favourite.add"]);
  const isAuthorizedAddAndRemoveSpotlight = useAuthorizer(["offers.social.spotlight"]);
  const isAuthorizedModifyAndCreate = useAuthorizer(["candidate.socialboard.modify"]);
  const isAuthorizedArchive = useAuthorizer(["candidate.socialboard.archive"]);
  const [{ loading: addLoading }, addOfferToFavorite] = useAddFavoriteOffer();
  const [, removeSocialOfferSpotlight] = useRemoveSocialOfferSpotlight();
  const [{ loading: deleteLoading }, deleteOfferFromFavorite] = useDeleteFavoriteOffer();

  const offerChallenges = useChallengesByTags(challenges, offer.tags);

  const callbackAddOfferToFavorite = async (selectedChallenges: IChallenge[]) => {
    if (selectedChallenges.length === 0) {
      return;
    }
    try {
      const challengeIds = selectedChallenges.map((challenge) => challenge._id);
      await addOfferToFavorite({
        offer: offer.offerId,
        challenges: challengeIds,
      });
      if (onUpdateOffer) {
        onUpdateOffer({ ...offer, favoriteChallenges: challengeIds });
      }
    } catch (error) {
      window.publish("feedback", {
        severity: "error",
        message: isAxiosError(error)
          ? error.message
          : "Une erreur est survenue lors de la proposition de l'offre. Veuillez contacter un administrateur.",
      });
    }
  };

  const handleClickAddFavorite = () => {
    void callbackAddOfferToFavorite(offerChallenges);
  };

  const handleClickRemoveFavorite = async () => {
    try {
      await deleteOfferFromFavorite({ offerId: offer.offerId });
      if (onUpdateOffer) {
        onUpdateOffer({ ...offer, favoriteChallenges: [] });
      }
    } catch (error) {
      window.publish("feedback", {
        severity: "error",
        message: isAxiosError(error)
          ? error.message
          : "Une erreur est survenue lors du retrait de l'offre au offres favorites. Veuillez contacter un administrateur.",
      });
    }
  };

  const handleClickRemoveSpotlight = (_id: ObjectId) => {
    return removeSocialOfferSpotlight(_id);
  };

  return {
    loading: addLoading || deleteLoading,
    archiveOffer,
    isAuthorizedAddFavorite,
    isAuthorizedAddAndRemoveSpotlight,
    isAuthorizedModifyAndCreate,
    isAuthorizedArchive,
    handleClickAddFavorite,
    handleClickRemoveFavorite,
    handleClickRemoveSpotlight,
  };
};
